import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { money } from "@/lib/format";

export const Route = createFileRoute("/admin/")({
  component: AdminDashboard,
});

const STATUS_LABELS: Record<string, string> = {
  nuevo: "Pago pendiente",
  pagado: "Pagados",
  intake_pendiente: "Formulario pendiente",
  en_proceso: "En proceso",
  esperando_cliente: "Esperando cliente",
  completado: "Completados",
  cancelado: "Cancelados",
};

// Mapeo de estado de orden → filtro en /admin/pedidos
const STATUS_TO_FILTER: Record<string, string> = {
  nuevo: "pago_pendiente",
  pagado: "pagados",
  intake_pendiente: "form_pendiente",
  en_proceso: "en_proceso",
  esperando_cliente: "esperando_cliente",
  completado: "completados",
  cancelado: "cancelados",
};

function AdminDashboard() {
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [kpis, setKpis] = useState<{ revenueMonth: number; paidMonth: number; urgentOpen: number; formPending: number; recent: any[] }>({
    revenueMonth: 0, paidMonth: 0, urgentOpen: 0, formPending: 0, recent: [],
  });

  useEffect(() => {
    (async () => {
      const statuses = ["nuevo","pagado","intake_pendiente","en_proceso","esperando_cliente","completado","cancelado"];
      const out: Record<string, number> = {};
      await Promise.all(statuses.map(async (s) => {
        const { count } = await supabase.from("orders").select("id", { count: "exact", head: true }).eq("status", s as any);
        out[s] = count ?? 0;
      }));
      setCounts(out);

      const startMonth = new Date();
      startMonth.setDate(1); startMonth.setHours(0,0,0,0);

      const { data: paidRows } = await supabase
        .from("orders").select("total,paid_at")
        .gte("paid_at", startMonth.toISOString())
        .not("paid_at", "is", null);
      const revenueMonth = (paidRows ?? []).reduce((a, r: any) => a + Number(r.total ?? 0), 0);
      const paidMonth = (paidRows ?? []).length;

      const { count: urgentOpen } = await supabase
        .from("orders").select("id", { count: "exact", head: true })
        .neq("status", "completado").neq("status", "cancelado")
        .or("urgent_manual.eq.true,operational_answers->>urgent.eq.true");

      const { count: formPending } = await supabase
        .from("orders").select("id", { count: "exact", head: true })
        .neq("status", "nuevo").neq("status", "cancelado")
        .eq("intake_completed", false);

      const { data: recent } = await supabase
        .from("orders")
        .select("id,status,total,created_at,customers(full_name,email)")
        .order("created_at", { ascending: false })
        .limit(8);

      setKpis({ revenueMonth, paidMonth, urgentOpen: urgentOpen ?? 0, formPending: formPending ?? 0, recent: recent ?? [] });
    })();
  }, []);

  return (
    <div>
      <h2 className="font-display text-xl font-semibold text-foreground">Dashboard</h2>

      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiLink label="Ingresos del mes" value={money(kpis.revenueMonth)} filter="pagados_mes" accent />
        <KpiLink label="Pagos del mes" value={String(kpis.paidMonth)} filter="pagados_mes" />
        <KpiLink label="Urgentes abiertos" value={String(kpis.urgentOpen)} filter="urgentes" />
        <KpiLink label="Formularios pendientes" value={String(kpis.formPending)} filter="form_pendiente" />
      </div>

      <h3 className="mt-10 font-display text-sm uppercase tracking-wider text-label-accent">Por estado</h3>
      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {Object.entries(counts).map(([k, v]) => (
          <Link
            key={k}
            to="/admin/pedidos"
            search={{ filter: STATUS_TO_FILTER[k] ?? "todos" }}
            className="rounded-md border border-hairline bg-surface p-4 text-left transition hover:border-primary/40 hover:bg-primary/5"
          >
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{STATUS_LABELS[k] ?? k.replace("_"," ")}</div>
            <div className="mt-1 font-display text-2xl font-semibold text-foreground">{v}</div>
          </Link>
        ))}
      </div>

      <h3 className="mt-10 font-display text-sm uppercase tracking-wider text-label-accent">Pedidos recientes</h3>
      <div className="mt-3 overflow-x-auto rounded-md border border-hairline">
        <table className="w-full text-sm">
          <thead className="bg-surface text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr><th className="p-3">Fecha</th><th className="p-3">Cliente</th><th className="p-3">Estado</th><th className="p-3">Total</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {kpis.recent.map((r: any) => (
              <tr key={r.id} className="border-t border-hairline">
                <td className="p-3 text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</td>
                <td className="p-3 text-foreground">{r.customers?.full_name} <span className="text-xs text-muted-foreground">· {r.customers?.email}</span></td>
                <td className="p-3 text-xs">{STATUS_LABELS[r.status] ?? r.status}</td>
                <td className="p-3">{money(r.total)}</td>
                <td className="p-3 text-right"><Link to="/admin/pedidos/$id" params={{ id: r.id }} className="text-xs text-primary hover:underline">Ver →</Link></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function KpiLink({ label, value, filter, accent }: { label: string; value: string; filter: string; accent?: boolean }) {
  return (
    <Link
      to="/admin/pedidos"
      search={{ filter }}
      className={`rounded-md border p-4 transition hover:border-primary/60 ${accent ? "border-primary/40 bg-primary/5 hover:bg-primary/10" : "border-hairline bg-surface hover:bg-primary/5"}`}
    >
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 font-display text-2xl font-semibold text-foreground">{value}</div>
    </Link>
  );
}
